import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchStudents } from '../../redux/studentSlice';
import { enrollStudent, fetchEnrolledCourses, fetchAllCourseEnrollments } from '../../redux/courseEnrollmentSlice';
import { fetchCourses } from '../../redux/coursesSlice';
import toast from 'react-hot-toast';

const CourseEnrollment = () => {
  const dispatch = useDispatch();
  const { courses, loading: coursesLoading } = useSelector((state) => state.courses);
  const { students, loading: studentsLoading } = useSelector((state) => state.students);
  const { 
    enrolledCourses, 
    allCourseEnrollments, 
    loading: enrollmentLoading, 
    error 
  } = useSelector((state) => state.courseEnrollment);

  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedCourse, setSelectedCourse] = useState('');
  const [showAllEnrollments, setShowAllEnrollments] = useState(false);

  useEffect(() => {
    dispatch(fetchCourses({ pageNumber: 1, pageSize: 100 }));
    dispatch(fetchStudents({ 
      pageNumber: 1, 
      pageSize: 100, 
      nameFilter: '', 
      campusId: '', 
      classId: '', 
      sectionId: '' 
    }));
    dispatch(fetchAllCourseEnrollments());
  }, [dispatch]);

  const handleEnroll = () => {
    if (!selectedStudent || !selectedCourse) {
      toast.error('Please select both a student and a course');
      return;
    }

    // Check if student is already enrolled in this course
    const isAlreadyEnrolled = allCourseEnrollments.some(
      enrollment => enrollment.studentId === selectedStudent && enrollment.courseId === selectedCourse
    );

    if (isAlreadyEnrolled) {
      toast.error('This student is already enrolled in this course');
      return;
    }

    dispatch(enrollStudent({ 
      courseId: selectedCourse, 
      studentId: selectedStudent 
    })).then((response) => {
      // Check if the action was fulfilled (success)
      if (response.meta && response.meta.requestStatus === 'fulfilled') {
        // Show success message
        toast.success('Student enrolled successfully!');
        
        // Clear selections
        setSelectedStudent('');
        setSelectedCourse('');
        
        // Refresh data
        dispatch(fetchAllCourseEnrollments());
      }
    });
  };

  const handleStudentChange = (e) => {
    const studentId = e.target.value;
    setSelectedStudent(studentId);
    if (studentId) {
      dispatch(fetchEnrolledCourses(studentId));
    }
  };

  // Filter out null and undefined enrollments
  const validEnrolledCourses = enrolledCourses.filter(enrollment => 
    enrollment && enrollment.courseId
  );

  // Get available courses (not already enrolled by selected student)
  const getAvailableCourses = () => {
    if (!selectedStudent) return courses;
    
    // Get IDs of courses the student is already enrolled in
    const enrolledCourseIds = validEnrolledCourses.map(enrollment => enrollment.courseId);
    
    // Filter out courses the student is already enrolled in
    return courses.filter(course => !enrolledCourseIds.includes(course.id));
  };

  const availableCourses = getAvailableCourses();

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4">Course Enrollment</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Student Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Student
          </label>
          <select
            value={selectedStudent}
            onChange={handleStudentChange}
            className="w-full p-2 border rounded-md"
            disabled={studentsLoading}
          >
            <option value="">Select a student</option>
            {students.map((student) => (
              <option key={student.student.id} value={student.student.id}>
                {student.student.firstName} {student.student.lastName}
              </option>
            ))}
          </select>
        </div>

        {/* Course Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Course
          </label>
          <select
            value={selectedCourse}
            onChange={(e) => setSelectedCourse(e.target.value)}
            className="w-full p-2 border rounded-md"
            disabled={coursesLoading || !selectedStudent}
          >
            <option value="">Select a course</option>
            {availableCourses.map((course) => (
              <option key={course.id} value={course.id}>
                {course.name}
              </option>
            ))}
          </select>
          {selectedStudent && availableCourses.length === 0 && (
            <p className="mt-2 text-sm text-amber-600">
              This student is already enrolled in all available courses.
            </p>
          )}
        </div>
      </div>

      {/* Enroll Button */}
      <button
        onClick={handleEnroll}
        disabled={enrollmentLoading || !selectedStudent || !selectedCourse}
        className={`px-4 py-2 rounded-md text-white ${
          enrollmentLoading || !selectedStudent || !selectedCourse 
            ? 'bg-gray-400 cursor-not-allowed' 
            : 'bg-[#241763] hover:bg-[#1a1254]'
        }`}
      >
        {enrollmentLoading ? 'Enrolling...' : 'Enroll Student'}
      </button>

      {/* Current Enrollments for Selected Student */}
      {selectedStudent && validEnrolledCourses.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-medium mb-2">Current Enrollments</h3>
          <div className="bg-gray-50 p-4 rounded-md">
            <ul className="list-disc pl-5">
              {validEnrolledCourses.map((enrollment, idx) => {
                const course = courses.find(c => c.id === enrollment.courseId);
                return (
                  <li key={idx}>
                    {course ? course.name : 'Unknown Course'}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Toggle View */}
      <div className="mt-6">
        <button 
          onClick={() => setShowAllEnrollments(!showAllEnrollments)}
          className="text-[#241763] underline hover:text-[#1a1254]"
        >
          {showAllEnrollments ? 'Show Student Enrollments' : 'Show All Enrollments'}
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* All Course Enrollments Table */}
      {showAllEnrollments && (
        <div className="mt-8">
          <h3 className="text-xl font-semibold mb-4">All Course Enrollments</h3>
          {allCourseEnrollments.length === 0 ? (
            <p>No course enrollments found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="py-2 px-4 border-b text-left">Course</th>
                    <th className="py-2 px-4 border-b text-left">Student</th>
                    <th className="py-2 px-4 border-b text-left">Email</th>
                    <th className="py-2 px-4 border-b text-left">Cover Image</th>
                  </tr>
                </thead>
                <tbody>
                  {allCourseEnrollments.map((enrollment, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="py-2 px-4 border-b">
                        {enrollment.courseName || 'Unknown Course'}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {`${enrollment.studentFirstName || ''} ${enrollment.studentLastName || ''}`}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {enrollment.studentEmail || 'N/A'}
                      </td>
                      <td className="py-2 px-4 border-b">
                        {enrollment.courseCoverImage ? (
                          <img 
                            src={enrollment.courseCoverImage} 
                            alt="Course cover" 
                            className="w-12 h-12 object-cover rounded" 
                          />
                        ) : (
                          'No image'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CourseEnrollment;